import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface Company {
    id: number;
    companyName: string;
    organizationNumber?: string;
    alternativeCompanyId?: string;
    brandConnectIdentifier?: string;
    leadTime?: number;
    commissionPercent?: number;
    priceModel?: string;
    priority?: string;
    companyType: CompanyType | null;
    deliveryTerm: DeliveryTerm | null;
    paymentTerm: PaymentTerm | null;
    shippingMethod: ShippingMethod | null;
    language: Language | null;
    purchasingCurrency: Currency | null;
    primaryPurchaser: User | null;
    resellerPriceList: PriceList | null;
    addresses: Address[];
    contactInfos: ContactInfo[];
    brands?: unknown[];
    selectedPriority?: ResellerPriority;
}

export interface CompanyType {
    id: number;
    companyTypeName: string;
    type: string;
    companySystemType: number;
}

export interface DeliveryTerm {
    id: number;
    code: string;
    name: string;
}

export interface PaymentTerm {
    id: number;
    code: string;
    name: string;
}

export interface ShippingMethod {
    id: number;
    name: string;
}

export interface Language {
    id: number;
    locale: string;
    name: string;
}

export interface Currency {
    id: number;
    code: string;
    name: string;
}

export interface User {
    id: number;
    firstName: string;
    lastName: string;
}

export interface PriceList {
    id: number;
    pricelistName: string;
}

export interface PricingModel {
    id: number;
    name: string;
    display: string;
}

export interface PricingModelsResponse {
    priceModels: PricingModel[];
}

export interface AddressType {
    id: number;
    typeName: string;
}

export interface Address {
    id?: number;
    addressType: AddressType;
    addressTypeId?: number;
    street?: string;
    postalCode?: string;
    city?: string;
    state?: string;
    country?: string;
    [key: string]: unknown;
}

export interface ContactInfoType {
    id: number;
    typeName: string;
}

export interface ContactInfo {
    id?: number;
    contactInfoType: ContactInfoType;
    value?: string;
    [key: string]: unknown;
}

export interface ResellerPriority {
    code: string;
    displayName: string;
}

export interface CompanyRequest {
    addresses: Address[] | undefined;
    alternativeCompanyId: string | undefined;
    brands: unknown[] | undefined;
    commissionPercent: number;
    companyName: string;
    companySystemType: number;
    companyTypeId: number | null;
    contactInfos: ContactInfo[] | undefined;
    deliveryTermsId: number | null;
    languageId: number | null;
    leadTime: number | undefined;
    organizationNumber: string | undefined;
    paymentTermsId: number | null;
    priceModel: string | undefined;
    primaryPurchaserId: number | null;
    priority: string | undefined;
    purchasingCurrencyId: number | null;
    resellerPriceListId: number | null;
    shippingMethodId: number | null;
    brandConnectIdentifier: string | undefined;
}

// API calls

export async function getCompany(companyId: number): Promise<Company> {
    return rms2Fetch<Company>(`companies/${companyId}`);
}

export async function createCompany(company: CompanyRequest): Promise<Company> {
    return rms2Fetch<Company>('companies', {
        method: 'POST',
        body: JSON.stringify(company),
    });
}

export async function updateCompany(companyId: number, company: CompanyRequest): Promise<Company> {
    return rms2Fetch<Company>(`companies/${companyId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...company, id: companyId }),
    });
}

export async function getCompanyTypes(): Promise<CompanyType[]> {
    const response = await rms2Fetch<{ companyTypes: CompanyType[] }>('companytypes');
    return response.companyTypes;
}

export async function getCurrencies(): Promise<Currency[]> {
    const response = await rms2Fetch<{ currencies: Currency[] }>('currencies');
    return response.currencies;
}

export async function getActiveUsers(): Promise<User[]> {
    const response = await rms2Fetch<{ users: User[] }>('activeusers');
    return response.users;
}

export async function getLanguages(): Promise<Language[]> {
    const response = await rms2Fetch<{ languages: Language[] }>('languages');
    return response.languages;
}

export async function getShippingMethods(): Promise<ShippingMethod[]> {
    const response = await rms2Fetch<{ shippingMethods: ShippingMethod[] }>('shippingmethods');
    return response.shippingMethods;
}

export async function getDeliveryTerms(): Promise<DeliveryTerm[]> {
    const response = await rms2Fetch<{ deliveryTerms: DeliveryTerm[] }>('deliveryterms');
    return response.deliveryTerms;
}

export async function getPaymentTerms(): Promise<PaymentTerm[]> {
    const response = await rms2Fetch<{ paymentTerms: PaymentTerm[] }>('paymentterms');
    return response.paymentTerms;
}

export async function getPricingModels(): Promise<PricingModelsResponse> {
    return rms2Fetch<PricingModelsResponse>('companies/pricemodels');
}

export async function getPriceListBriefs(): Promise<PriceList[]> {
    const response = await rms2Fetch<{ priceLists: PriceList[] }>('pricelists');
    return response.priceLists;
}

export async function getAddressTypes(): Promise<AddressType[]> {
    const response = await rms2Fetch<{ addressTypes: AddressType[] }>('addresstypes');
    return response.addressTypes;
}

export async function getContactInfoTypes(): Promise<ContactInfoType[]> {
    const response = await rms2Fetch<{ contactInfoTypes: ContactInfoType[] }>('contactinfotypes');
    return response.contactInfoTypes;
}
